// src/utils/firebaseErrorHandler.js
import { Logger } from "./logger";

/**
 * Firebase-specific error handler with retry logic and user-friendly messages
 */

/**
 * Firebase error codes that may benefit from retry
 */
const RETRYABLE_ERROR_CODES = [
  "unavailable",
  "deadline-exceeded",
  "resource-exhausted",
  "aborted",
  "internal",
  "cancelled",
];

/**
 * Firebase error codes that indicate infrastructure issues
 * 
const INFRASTRUCTURE_ERROR_CODES = [
  'failed-precondition',
  'permission-denied',
  'not-found',
  'already-exists',
  'out-of-range',
  'unimplemented',
  'data-loss'
];
 */

/**
 * Firebase error codes that should trigger fallback behavior
 */
const FALLBACK_TRIGGER_CODES = [
  "failed-precondition",
  "unavailable",
  "deadline-exceeded",
  "resource-exhausted",
];

/**
 * Map Firebase error codes to user-friendly messages
 */
const ERROR_MESSAGES = {
  "failed-precondition": {
    user: "Advanced search is temporarily unavailable. We've switched to simplified search.",
    dev: "Firestore index missing or not ready. Check Firebase Console for index status.",
    searchSpecific:
      "This search combination requires database indexing. Using alternative search method.",
  },
  "permission-denied": {
    user: "You don't have permission to access this data.",
    dev: "Firestore security rules are blocking this query.",
  },
  unavailable: {
    user: "Service is temporarily unavailable. Please try again in a moment.",
    dev: "Firebase service is temporarily unavailable.",
  },
  "deadline-exceeded": {
    user: "Request is taking longer than expected. Please try again.",
    dev: "Query timeout exceeded. Consider optimizing the query or adding indexes.",
  },
  "resource-exhausted": {
    user: "Service is currently at capacity. Please try again in a few minutes.",
    dev: "Firebase quota exceeded or rate limit hit.",
  },
  "not-found": {
    user: "The requested data was not found.",
    dev: "Document or collection not found.",
  },
  "already-exists": {
    user: "This item already exists.",
    dev: "Trying to create a document that already exists.",
  },
  "invalid-argument": {
    user: "Invalid search criteria provided.",
    dev: "Invalid query parameters or document data.",
  },
  unauthenticated: {
    user: "Please sign in to access this feature.",
    dev: "User is not authenticated.",
  },
  aborted: {
    user: "Operation was cancelled. Please try again.",
    dev: "Transaction aborted due to conflicts.",
  },
  "out-of-range": {
    user: "Search parameters are out of valid range.",
    dev: "Query parameters exceed valid ranges.",
  },
  unimplemented: {
    user: "This feature is not yet available.",
    dev: "Requested operation is not implemented.",
  },
  internal: {
    user: "An internal error occurred. Please try again.",
    dev: "Internal Firebase error.",
  },
  cancelled: {
    user: "Operation was cancelled.",
    dev: "Request was cancelled by client or server.",
  },
  "data-loss": {
    user: "Data integrity issue detected. Please contact support.",
    dev: "Unrecoverable data loss or corruption.",
  },
};

/**
 * Default retry configuration
 */
const DEFAULT_RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  exponentialFactor: 2,
  jitter: 0.1, // 10% jitter
};

/**
 * Enhanced Firebase error class with additional metadata
 */
export class FirebaseEnhancedError extends Error {
  constructor(originalError, context = {}) {
    const errorInfo = parseFirebaseError(originalError);
    super(errorInfo.userMessage);

    this.name = "FirebaseEnhancedError";
    this.code = errorInfo.code;
    this.originalError = originalError;
    this.userMessage = errorInfo.userMessage;
    this.developerMessage = errorInfo.developerMessage;
    this.isRetryable = errorInfo.isRetryable;
    this.shouldFallback = errorInfo.shouldFallback;
    this.context = context;
    this.timestamp = new Date().toISOString();

    // Add operation tracking
    this.operationType = context.operationType || "unknown";
    this.service = context.service || "firebase";
    this.collection = context.collection || null;
    this.operation = context.operation || null;
    this.userId = context.userId || null;
    this.sessionId = context.sessionId || null;
  }
}

/**
 * Parse Firebase error and extract relevant information
 */
export const parseFirebaseError = (error) => {
  const code = error?.code || "unknown";
  const baseCode = code.replace("firestore/", "").replace("firebase/", "");

  const errorConfig = ERROR_MESSAGES[baseCode] || {
    user: "An unexpected error occurred. Please try again.",
    dev: `Firebase error: ${code}`,
  };

  return {
    code: baseCode,
    userMessage: errorConfig.user,
    developerMessage: errorConfig.dev,
    isRetryable: RETRYABLE_ERROR_CODES.includes(baseCode),
    shouldFallback: FALLBACK_TRIGGER_CODES.includes(baseCode),
    originalMessage: error?.message || "Unknown error",
  };
};

/**
 * Check if an error should trigger fallback behavior
 */
export const shouldTriggerFallback = (error) => {
  const parsedError = parseFirebaseError(error);
  return parsedError.shouldFallback;
};

/**
 * Check if an error is retryable
 */
export const isRetryableError = (error) => {
  const parsedError = parseFirebaseError(error);
  return parsedError.isRetryable;
};

/**
 * Calculate retry delay with exponential backoff and jitter
 */
const calculateRetryDelay = (attempt, config = DEFAULT_RETRY_CONFIG) => {
  const exponentialDelay = Math.min(
    config.baseDelay * Math.pow(config.exponentialFactor, attempt - 1),
    config.maxDelay
  );

  // Add jitter to prevent thundering herd
  const jitter = exponentialDelay * config.jitter * (Math.random() - 0.5);

  return Math.max(100, exponentialDelay + jitter); // Minimum 100ms delay
};

/**
 * Sleep utility for delays
 */
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Retry wrapper for Firebase operations with exponential backoff
 */
export const withRetry = async (operation, context = {}, retryConfig = {}) => {
  const config = { ...DEFAULT_RETRY_CONFIG, ...retryConfig };
  let lastError;
  const startTime = Date.now();

  // Add session tracking
  if (!context.sessionId) {
    context.sessionId = generateSessionId();
  }

  for (let attempt = 1; attempt <= config.maxRetries + 1; attempt++) {
    const attemptStartTime = Date.now();

    try {
      Logger.log(
        "debug",
        "Attempting Firebase operation",
        {
          attempt,
          maxRetries: config.maxRetries,
          operationName: context.operationName || "unknown",
          service: context.service || "firebase",
          collection: context.collection,
          operation: context.operation,
          userId: context.userId,
          sessionId: context.sessionId,
        },
        "FirebaseErrorHandler"
      );

      const result = await operation();
      const duration = Date.now() - attemptStartTime;
      const totalDuration = Date.now() - startTime;

      if (attempt > 1) {
        Logger.log(
          "info",
          "Firebase operation succeeded after retry",
          {
            attempt,
            operationName: context.operationName || "unknown",
            service: context.service || "firebase",
            duration,
            totalDuration,
            sessionId: context.sessionId,
          },
          "FirebaseErrorHandler"
        );
      } else {
        Logger.log(
          "debug",
          "Firebase operation succeeded on first attempt",
          {
            operationName: context.operationName || "unknown",
            service: context.service || "firebase",
            duration,
            sessionId: context.sessionId,
          },
          "FirebaseErrorHandler"
        );
      }

      return result;
    } catch (error) {
      lastError = error;
      const attemptDuration = Date.now() - attemptStartTime;
      const parsedError = parseFirebaseError(error);

      // Enhanced error logging with more details
      Logger.log(
        "warn",
        "Firebase operation failed",
        {
          attempt,
          errorCode: parsedError.code,
          originalErrorCode: error?.code,
          errorMessage: error?.message,
          stack: error?.stack?.split("\n")[0], // First line of stack trace
          isRetryable: parsedError.isRetryable,
          shouldFallback: parsedError.shouldFallback,
          operationName: context.operationName || "unknown",
          service: context.service || "firebase",
          collection: context.collection,
          operation: context.operation,
          userId: context.userId,
          sessionId: context.sessionId,
          attemptDuration,
          totalDuration: Date.now() - startTime,
          contextData: context,
          error: parsedError.developerMessage,
          timestamp: new Date().toISOString(),
        },
        "FirebaseErrorHandler"
      );

      // Log specific error details for debugging
      if (error?.code) {
        Logger.log(
          "debug",
          "Raw Firebase error details",
          {
            code: error.code,
            message: error.message,
            name: error.name,
            details: error.details || "No details available",
            cause: error.cause || "No cause available",
            service: context.service || "firebase",
            operation: context.operation,
            sessionId: context.sessionId,
          },
          "FirebaseErrorHandler"
        );
      }

      // Don't retry on the last attempt or if error is not retryable
      if (attempt > config.maxRetries || !parsedError.isRetryable) {
        break;
      }

      // Calculate and apply delay before next attempt
      const delay = calculateRetryDelay(attempt, config);
      Logger.log(
        "debug",
        "Waiting before retry",
        {
          delay,
          attempt,
          sessionId: context.sessionId,
        },
        "FirebaseErrorHandler"
      );
      await sleep(delay);
    }
  }

  // All retries exhausted, throw enhanced error
  const enhancedError = new FirebaseEnhancedError(lastError, context);
  const totalDuration = Date.now() - startTime;

  Logger.log(
    "error",
    "Firebase operation failed after all retries",
    {
      attempts: config.maxRetries + 1,
      finalError: enhancedError.code,
      originalError: lastError?.code || "unknown",
      originalMessage: lastError?.message || "unknown",
      operationName: context.operationName || "unknown",
      service: context.service || "firebase",
      collection: context.collection,
      operation: context.operation,
      userId: context.userId,
      sessionId: context.sessionId,
      totalDuration,
      contextData: context,
      timestamp: new Date().toISOString(),
    },
    "FirebaseErrorHandler"
  );

  throw enhancedError;
};

/**
 * Create a Firebase operation wrapper with consistent error handling
 */
export const createFirebaseOperation = (operationName) => {
  return async (firebaseOperation, fallbackOperation = null, options = {}) => {
    const context = { operationName, ...options.context };
    const retryConfig = options.retryConfig || {};

    try {
      return await withRetry(firebaseOperation, context, retryConfig);
    } catch (error) {
      if (error instanceof FirebaseEnhancedError) {
        // Log the error for monitoring
        Logger.log(
          "error",
          "Firebase operation failed with enhanced error",
          {
            operation: operationName,
            code: error.code,
            userMessage: error.userMessage,
            shouldFallback: error.shouldFallback,
            context,
          },
          "FirebaseErrorHandler"
        );

        // Attempt fallback if available and error indicates we should
        if (fallbackOperation && error.shouldFallback) {
          Logger.log(
            "info",
            "Executing fallback operation",
            {
              operation: operationName,
              originalError: error.code,
            },
            "FirebaseErrorHandler"
          );

          try {
            const result = await fallbackOperation();
            Logger.log(
              "info",
              "Fallback operation successful",
              {
                operation: operationName,
              },
              "FirebaseErrorHandler"
            );
            return result;
          } catch (fallbackError) {
            Logger.log(
              "error",
              "Fallback operation also failed",
              {
                operation: operationName,
                originalError: error.code,
                fallbackError: fallbackError.message,
              },
              "FirebaseErrorHandler"
            );
            // Throw the original enhanced error, not the fallback error
            throw error;
          }
        }

        throw error;
      } else {
        // Unexpected error type, wrap it
        throw new FirebaseEnhancedError(error, context);
      }
    }
  };
};

/**
 * Create search-specific error handler
 */
export const createSearchErrorHandler = () => {
  return createFirebaseOperation("search");
};

/**
 * Utility to check if indexes are likely building
 */
export const isLikelyIndexBuilding = (error) => {
  const parsedError = parseFirebaseError(error);
  return (
    parsedError.code === "failed-precondition" &&
    error?.message?.includes("index")
  );
};

/**
 * Extract missing index information from error message
 */
export const extractMissingIndexInfo = (error) => {
  if (!error?.message) return null;

  const message = error.message;
  const indexMatch = message.match(
    /https:\/\/console\.firebase\.google\.com[^\s]+/
  );

  return {
    hasIndexUrl: !!indexMatch,
    indexUrl: indexMatch ? indexMatch[0] : null,
    isCompoundIndex: message.includes("composite index"),
    isSingleFieldIndex: message.includes("single field index"),
    fields: extractFieldsFromErrorMessage(message),
  };
};

/**
 * Extract field information from error message
 */
const extractFieldsFromErrorMessage = (message) => {
  const fields = [];

  // Look for field patterns like "fieldName" ASC/DESC
  const fieldMatches = message.match(/"([^"]+)"\s+(ASC|DESC|CONTAINS)/g);
  if (fieldMatches) {
    fieldMatches.forEach((match) => {
      const [, fieldName, order] = match.match(
        /"([^"]+)"\s+(ASC|DESC|CONTAINS)/
      );
      fields.push({ fieldName, order });
    });
  }

  return fields;
};

/**
 * Enhanced failed-precondition error handler for search operations
 */
export const handleSearchFailedPrecondition = (
  error,
  filters,
  context = {}
) => {
  const indexInfo = extractMissingIndexInfo(error);

  Logger.log(
    "warn",
    "Search failed due to missing index",
    {
      indexInfo,
      filters,
      context,
    },
    "FirebaseErrorHandler"
  );

  // Determine specific error type and message
  if (indexInfo?.hasIndexUrl) {
    Logger.log(
      "info",
      "Missing index URL available",
      {
        indexUrl: indexInfo.indexUrl,
        fields: indexInfo.fields,
      },
      "FirebaseErrorHandler"
    );

    return {
      type: "missing-index",
      userMessage:
        "This search combination is being optimized. Please try a simpler search.",
      developerMessage: `Missing Firestore index. Create index at: ${indexInfo.indexUrl}`,
      indexUrl: indexInfo.indexUrl,
      suggestedFields: indexInfo.fields,
      shouldFallback: true,
    };
  }

  // Check for common problematic patterns
  const arrayFilters = Object.entries(filters).filter(
    ([key, value]) =>
      Array.isArray(value) &&
      value.length > 0 &&
      [
        "instruments",
        "specializations",
        "ageGroups",
        "genres",
        "teachingStyles",
      ].includes(key)
  );

  if (arrayFilters.length > 1) {
    return {
      type: "multiple-array-filters",
      userMessage: "Multiple category filters detected. Simplifying search...",
      developerMessage:
        "Multiple array-contains-any filters not supported by Firestore",
      problematicFilters: arrayFilters.map(([key, value]) => ({
        field: key,
        count: value.length,
      })),
      shouldFallback: true,
    };
  }

  // Check for OR query issues
  if (filters.instruments?.length === 1) {
    return {
      type: "or-query-limitation",
      userMessage: "Instrument search optimized. Results may vary slightly.",
      developerMessage:
        "OR query for instrument/instruments fields not supported",
      shouldFallback: true,
    };
  }

  // Generic failed-precondition
  return {
    type: "generic-precondition",
    userMessage:
      "Search parameters not fully supported. Using alternative method.",
    developerMessage:
      "Failed-precondition error without specific index information",
    shouldFallback: true,
  };
};

/**
 * Get user-friendly error message from any Firebase error
 */
export const getUserFriendlyErrorMessage = (error) => {
  if (error instanceof FirebaseEnhancedError) {
    return error.userMessage;
  }

  const parsedError = parseFirebaseError(error);
  return parsedError.userMessage;
};

/**
 * Export commonly used constants
 */
/**
 * Generate unique session ID for tracking related operations
 */
const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Create operation context with common tracking fields
 */
export const createOperationContext = (
  operationName,
  additionalContext = {}
) => {
  const context = {
    operationName,
    sessionId: generateSessionId(),
    timestamp: new Date().toISOString(),
    ...additionalContext,
  };

  // Add user context if available
  if (typeof window !== "undefined" && window.$auth?.currentUser) {
    context.userId = window.$auth.currentUser.uid;
  }

  return context;
};

/**
 * Firebase service type mapping
 */
export const FIREBASE_SERVICES = {
  FIRESTORE: "firestore",
  AUTH: "auth",
  STORAGE: "storage",
  FUNCTIONS: "functions",
  ANALYTICS: "analytics",
};

/**
 * Firebase operation types
 */
export const OPERATION_TYPES = {
  READ: "read",
  write: "write",
  delete: "delete",
  query: "query",
  auth: "auth",
  upload: "upload",
  download: "download",
  call: "call",
};

/**
 * Enhanced error monitoring with categorization
 */
export const monitorFirebaseError = (
  error,
  operation,
  additionalContext = {}
) => {
  const parsedError = parseFirebaseError(error);
  const context = {
    ...additionalContext,
    timestamp: new Date().toISOString(),
    userAgent:
      typeof window !== "undefined" ? window.navigator?.userAgent : "server",
  };

  // Categorize error type
  const errorCategory = categorizeError(parsedError.code);

  Logger.log(
    "error",
    "Firebase error monitored",
    {
      operation,
      code: parsedError.code,
      category: errorCategory,
      service: context.service || "unknown",
      collection: context.collection,
      operationType: context.operationType,
      isRetryable: parsedError.isRetryable,
      shouldFallback: parsedError.shouldFallback,
      userMessage: parsedError.userMessage,
      developerMessage: parsedError.developerMessage,
      context,
    },
    "FirebaseErrorHandler"
  );
};

/**
 * Categorize Firebase errors for better analysis
 */
const categorizeError = (errorCode) => {
  const networkErrors = ["unavailable", "deadline-exceeded", "cancelled"];
  const permissionErrors = ["permission-denied", "unauthenticated"];
  const dataErrors = ["not-found", "already-exists", "invalid-argument"];
  const quotaErrors = ["resource-exhausted"];
  const configErrors = ["failed-precondition", "unimplemented"];
  const systemErrors = ["internal", "aborted", "data-loss"];

  if (networkErrors.includes(errorCode)) return "network";
  if (permissionErrors.includes(errorCode)) return "permission";
  if (dataErrors.includes(errorCode)) return "data";
  if (quotaErrors.includes(errorCode)) return "quota";
  if (configErrors.includes(errorCode)) return "configuration";
  if (systemErrors.includes(errorCode)) return "system";

  return "unknown";
};

export const FIREBASE_ERROR_CODES = {
  FAILED_PRECONDITION: "failed-precondition",
  PERMISSION_DENIED: "permission-denied",
  NOT_FOUND: "not-found",
  UNAVAILABLE: "unavailable",
  DEADLINE_EXCEEDED: "deadline-exceeded",
  RESOURCE_EXHAUSTED: "resource-exhausted",
  UNAUTHENTICATED: "unauthenticated",
  INVALID_ARGUMENT: "invalid-argument",
};

export default {
  FirebaseEnhancedError,
  parseFirebaseError,
  shouldTriggerFallback,
  isRetryableError,
  withRetry,
  createFirebaseOperation,
  createSearchErrorHandler,
  monitorFirebaseError,
  isLikelyIndexBuilding,
  getUserFriendlyErrorMessage,
  FIREBASE_ERROR_CODES,
};
